// 大五人格测评分析服务

import { BigFiveQuestion, BigFiveDimension, bigFiveDimensions } from '../data/bigFiveQuestions';

// 用户答案接口
export interface BigFiveAnswer {
  questionId: number;
  selectedOption: number; // 0-4分制评分
}

// 维度得分结果
export interface DimensionScore {
  dimension: BigFiveDimension['key'];
  name: string;
  score: number; // 0-40分 (每个维度10题，每题最高4分)
  percentage: number; // 百分比 (0-100)
  level: 'very_low' | 'low' | 'moderate' | 'high' | 'very_high';
  description: string;
}

// 完整测评结果
export interface BigFiveResult {
  scores: DimensionScore[];
  overallProfile: string;
  recommendations: string[];
  completedAt: string;
}

// 计算单个维度得分
export function calculateDimensionScore(
  questions: BigFiveQuestion[],
  answers: BigFiveAnswer[],
  dimension: BigFiveDimension['key']
): DimensionScore {
  const dimensionQuestions = questions.filter(q => q.dimension === dimension);
  const dimensionAnswers = answers.filter(a => 
    dimensionQuestions.some(q => q.id === a.questionId)
  );

  let totalScore = 0;
  const maxScore = dimensionQuestions.length * 4; // 每题最高4分

  dimensionAnswers.forEach(answer => {
    const question = dimensionQuestions.find(q => q.id === answer.questionId);
    if (question) {
      let score = answer.selectedOption;
      
      // 计分逻辑：0分倾向A选项，4分倾向B选项
      // 如果正向选项是A，需要反转分数让选A得高分
      if (question.positiveOption === 'a') {
        score = 4 - score;
      }
      
      totalScore += score;
    }
  });
  
  const percentage = Math.round((totalScore / maxScore) * 100);
  const level = getScoreLevel(percentage);
  const dimensionInfo = bigFiveDimensions.find(d => d.key === dimension)!;
  
  return {
    dimension,
    name: dimensionInfo.name,
    score: totalScore,
    percentage,
    level,
    description: getDimensionDescription(dimension, level)
  };
}

// 根据百分比确定水平等级
function getScoreLevel(percentage: number): DimensionScore['level'] {
  if (percentage >= 80) return 'very_high';
  if (percentage >= 60) return 'high';
  if (percentage >= 40) return 'moderate';
  if (percentage >= 20) return 'low';
  return 'very_low';
}

// 获取维度描述
function getDimensionDescription(dimension: BigFiveDimension['key'], level: DimensionScore['level']): string {
  const descriptions = {
    openness: {
      very_high: '你极度开放，热衷于探索新想法、新体验，具有强烈的创造力和想象力。',
      high: '你相当开放，喜欢尝试新事物，对艺术和抽象概念有较强的欣赏能力。',
      moderate: '你在开放性方面表现适中，既能接受新事物，也重视传统和稳定。',
      low: '你倾向于保守，更喜欢熟悉的环境和传统的做事方式。',
      very_low: '你非常保守，强烈偏好传统和常规，对新奇事物持谨慎态度。'
    },
    conscientiousness: {
      very_high: '你极度自律和有组织，总是能够很好地完成任务和达成目标。',
      high: '你相当自律，做事有条理，能够坚持完成重要任务。',
      moderate: '你在自律性方面表现适中，有时很有组织，有时比较随性。',
      low: '你倾向于比较随性，不太喜欢严格的计划和规则。',
      very_low: '你非常随性和灵活，不喜欢被计划和规则束缚。'
    },
    extraversion: {
      very_high: '你极度外向，非常善于社交，喜欢成为关注的焦点。',
      high: '你相当外向，享受社交活动，在人群中感到舒适。',
      moderate: '你在外向性方面表现适中，既享受社交也需要独处时间。',
      low: '你倾向于内向，更喜欢小群体或一对一的交流。',
      very_low: '你非常内向，更喜欢独处，在大型社交场合会感到不适。'
    },
    agreeableness: {
      very_high: '你极度友善和合作，总是优先考虑他人的需求和感受。',
      high: '你相当友善，善于合作，通常能与他人和谐相处。',
      moderate: '你在宜人性方面表现适中，既能合作也能坚持自己的立场。',
      low: '你倾向于竞争性思维，更关注自己的利益和目标。',
      very_low: '你非常独立和竞争性，不太在意他人的看法和感受。'
    },
    neuroticism: {
      very_high: '你情绪波动较大，容易感到焦虑、压力和负面情绪。',
      high: '你有时会感到焦虑和压力，情绪相对不够稳定。',
      moderate: '你的情绪稳定性适中，大多数时候能够保持平静。',
      low: '你情绪相当稳定，很少感到焦虑或过度担心。',
      very_low: '你情绪极度稳定，几乎不会被压力或负面事件影响。'
    }
  };

  return descriptions[dimension][level];
}

// 生成完整的测评结果
export function generateBigFiveResult(
  questions: BigFiveQuestion[],
  answers: BigFiveAnswer[]
): BigFiveResult {
  const scores = bigFiveDimensions.map(dimension => 
    calculateDimensionScore(questions, answers, dimension.key)
  );

  const overallProfile = generateOverallProfile(scores);
  const recommendations = generateRecommendations(scores);

  return {
    scores,
    overallProfile,
    recommendations,
    completedAt: new Date().toISOString()
  };
}

// 生成整体人格画像
function generateOverallProfile(scores: DimensionScore[]): string {
  const highestDimension = scores.reduce((prev, current) => 
    prev.percentage > current.percentage ? prev : current
  );
  
  const lowestDimension = scores.reduce((prev, current) => 
    prev.percentage < current.percentage ? prev : current
  );

  return `你的人格特征中，${highestDimension.name}最为突出（${highestDimension.percentage}%），
这表明${highestDimension.description.toLowerCase()}

相对而言，你在${lowestDimension.name}方面的表现较为温和（${lowestDimension.percentage}%），
${lowestDimension.description.toLowerCase()}

这种人格组合使你成为一个独特的个体，在不同情境下会展现出不同的行为模式和偏好。`;
}

// 生成个性化建议
function generateRecommendations(scores: DimensionScore[]): string[] {
  const recommendations: string[] = [];

  scores.forEach(score => {
    switch (score.dimension) {
      case 'openness':
        if (score.level === 'very_high' || score.level === 'high') {
          recommendations.push('继续保持对新事物的好奇心，但也要学会在创新和实用性之间找到平衡。');
        } else if (score.level === 'low' || score.level === 'very_low') {
          recommendations.push('尝试偶尔走出舒适圈，接触一些新的想法或体验，这有助于个人成长。');
        }
        break;
      
      case 'conscientiousness':
        if (score.level === 'very_high' || score.level === 'high') {
          recommendations.push('你的自律性很强，但记得适当放松，避免过度完美主义带来的压力。');
        } else if (score.level === 'low' || score.level === 'very_low') {
          recommendations.push('考虑建立一些简单的日常习惯和目标，这有助于提高生活和工作效率。');
        }
        break;
      
      case 'extraversion':
        if (score.level === 'very_high' || score.level === 'high') {
          recommendations.push('你的社交能力很强，但也要确保给自己留出独处和反思的时间。');
        } else if (score.level === 'low' || score.level === 'very_low') {
          recommendations.push('尝试参与一些小型社交活动，逐步扩展你的社交圈子。');
        }
        break;
      
      case 'agreeableness':
        if (score.level === 'very_high' || score.level === 'high') {
          recommendations.push('你很善于合作，但也要学会在必要时坚持自己的立场和边界。');
        } else if (score.level === 'low' || score.level === 'very_low') {
          recommendations.push('尝试更多地考虑他人的感受和需求，这有助于建立更好的人际关系。');
        }
        break;
      
      case 'neuroticism':
        if (score.level === 'very_high' || score.level === 'high') {
          recommendations.push('学习一些压力管理和情绪调节技巧，如冥想、运动或寻求专业帮助。');
        } else if (score.level === 'low' || score.level === 'very_low') {
          recommendations.push('你的情绪稳定性很好，可以考虑帮助那些情绪波动较大的朋友。');
        }
        break;
    }
  });

  return recommendations;
}

// 数据验证
export function validateAnswers(questions: BigFiveQuestion[], answers: BigFiveAnswer[]): boolean {
  // 检查是否所有题目都有答案
  const questionIds = questions.map(q => q.id);
  const answeredIds = answers.map(a => a.questionId);
  
  return questionIds.every(id => answeredIds.includes(id));
}